import { SupabaseClient } from "@supabase/supabase-js";
import * as Sentry from "@sentry/nextjs";
import { PaymentRecordStatus, PaymentTier } from "@/app/types/payment";
import { Work } from "@/app/types/book";
import { Order, Product } from "@/app/types/products";
import { invalidateCacheEntry } from "@/lib/cache";

/**
 * Record a successful payment and create order details for all products within the tier.
 *
 * @param client - Supabase client
 * @param userId - Purchasing user
 * @param paymentTier - Tier purchased
 * @param stripePaymentSession - Raw session data from Stripe
 * @param getProductTier - Helper to look up the primary product for the tier
 * @param getProductsByPaymentTier - Helper to list all tier products
 * @param getWorkByFriendlyId - Resolver for associating a work with the order
 * @param workFriendlyId - Optional friendly ID of the work being upgraded
 * @param productCodeId - Optional product code ID for redeeming a product code
 * @param specificProduct - Optional specific product to use instead of the tier's primary product (for product code redemption)
 */
export async function handleSuccessfulPayment(
  client: SupabaseClient,
  userId: string,
  paymentTier: PaymentTier,
  stripePaymentSession: any,
  getProductTier: (tier: PaymentTier) => Promise<Product | null>,
  getProductsByPaymentTier: (tier: PaymentTier) => Promise<Product[] | null>,
  getWorkByFriendlyId: (
    friendlyId: string,
    userId: string
  ) => Promise<Work | null>,
  workFriendlyId?: string,
  productCodeId?: string,
  specificProduct?: Product
): Promise<any> {
  try {
    Sentry.addBreadcrumb({
      message: `[handleSuccessfulPayment] start user ${userId} tier ${paymentTier}`,
      level: 'info',
    });
    // Confirm the user exists in the user_profiles table before creating an order
    const { data: userCheck, error: userCheckError } = await client
      .from("user_profiles")
      .select("id")
      .eq("id", userId)
      .maybeSingle();

    if (userCheckError) {
      console.error(
        `[handleSuccessfulPayment] Error verifying user ${userId}:`,
        userCheckError,
      );
      Sentry.captureException(userCheckError);
    } else if (!userCheck) {
      const msg = `[handleSuccessfulPayment] User ${userId} not found in user_profiles`;
      console.error(msg);
      Sentry.captureMessage(msg, "warning");
    }

    if (!stripePaymentSession.id) {
      throw new Error("[handleSuccessfulPayment] No payment session id");
    }

    if (
      stripePaymentSession.amount_total === undefined ||
      stripePaymentSession.amount_total === null
    ) {
      // Zero is a valid amount (free tier) but undefined/null indicates an
      // incomplete session object
      throw new Error("[handleSuccessfulPayment] No payment session amount");
    }

    if (!stripePaymentSession.currency) {
      throw new Error("[handleSuccessfulPayment] No payment session currency");
    }

    // Use the specific product if provided (for product code redemption),
    // otherwise get the tier's primary product
    const tieredProductDefinition = specificProduct || await getProductTier(paymentTier);

    if (!tieredProductDefinition) {
      throw new Error("[handleSuccessfulPayment] No product tier found");
    }

    const { data: existingOrder, error } = await client
      .from("orders")
      .select("*")
      .eq("stripe_payment_id", stripePaymentSession.id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (existingOrder) {
      throw new Error("Order already exists");
    }

    let work: Work | null = null;
    if (workFriendlyId) {
      work = await getWorkByFriendlyId(workFriendlyId, userId);
      if (!work) {
        throw new Error("Work not found");
      }
    }

    const paymentStatus = PaymentRecordStatus.Paid.toString();
    const { data: newOrder, error: orderError } = await client
      .from("orders")
      .insert({
        user_id: userId,
        stripe_payment_id: stripePaymentSession.id,
        status: paymentStatus,
        total_price: stripePaymentSession.amount_total / 100,
        total_tax: stripePaymentSession.amount_tax / 100,
        work_id: work?.id || null,
        product_id: tieredProductDefinition.id,
        product_code_id: productCodeId || null,
        payload: JSON.stringify(stripePaymentSession),
      })
      .select()
      .single() as { data: Order | null; error: any };

    if (orderError) {
      throw orderError;
    }

    if (!newOrder) {
      throw new Error("Failed to create order");
    }

    const productsWithinPaymentTier = await getProductsByPaymentTier(paymentTier);

    if (!productsWithinPaymentTier) {
      throw new Error("No products found for payment tier");
    }

    for (const product of productsWithinPaymentTier) {
      const { data: newOrderDetail, error: orderDetailError } = await client
        .from("order_items")
        .insert({
          order_id: newOrder.id,
          product_id: product.id,
          product_code_id: productCodeId || null,
          quantity: 1,
          price: 0,
          tax_amount: 0,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (orderDetailError) {
        throw orderDetailError;
      }

      console.debug(
        `[handleSuccessfulPayment] Created order item for product '${product.name}' (Id: ${newOrderDetail.id})`
      );
    }

    Sentry.withScope((scope) => {
      scope.setContext("payment", {
        userId,
        paymentTier,
        sessionId: stripePaymentSession.id,
        workFriendlyId: workFriendlyId || null,
      });
      Sentry.captureMessage("handleSuccessfulPayment completed", "info");
    });

    // Invalidate the cached works list for this user so newly created works
    // appear immediately in the UI after payment completes.
    invalidateCacheEntry("WorkDb", "getWorksForUser", `user-works:${userId}`);

    return newOrder;
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setContext("payment", {
        userId,
        paymentTier,
        sessionId: stripePaymentSession.id,
        workFriendlyId: workFriendlyId || null,
      });
      Sentry.captureException(err);
    });
    throw err;
  }
}

/**
 * Attach a work to an existing order after payment.
 *
 * @param client - Supabase client
 * @param workId - Work being linked
 * @param orderId - Order to update
 * @returns Updated order or null
 */
export async function associatedWorkWithOrderId(
  client: SupabaseClient,
  workId: string,
  orderId: string
): Promise<Order | null> {
  const { data: existingOrder, error: existingOrderError } = await client
    .from("orders")
    .select("*")
    .eq("work_id", workId)
    .maybeSingle() as { data: Order | null; error: any };

  if (existingOrderError) {
    throw existingOrderError;
  }

  if (existingOrder) {
    throw new Error("Work already has an order");
  }

  const { data, error } = await client
    .from("orders")
    .update({ work_id: workId })
    .eq("id", orderId)
    .select()
    .single() as { data: Order | null; error: any };

  if (error) {
    throw error;
  }

  console.debug(`[associatedWorkWithOrderId] Associated work '${workId}' with order '${orderId}'`);

  return data;
}

/**
 * Record information about a failed payment attempt.
 *
 * @param client - Supabase client
 * @param userId - User who attempted payment
 * @param paymentTier - Tier being purchased
 * @param paymentSession - Raw session data from Stripe
 * @param workId - Optional associated work
 */
export async function handleFailedPayment(
  client: SupabaseClient,
  userId: string,
  paymentTier: PaymentTier,
  paymentSession: any,
  workId?: string
): Promise<any> {
  if (!paymentSession.id) {
    throw new Error("[handleFailedPayment] No payment session id");
  }

  if (
    paymentSession.amount_total === undefined ||
    paymentSession.amount_total === null
  ) {
    // Zero payments are valid (free tier), but missing values indicate a
    // malformed payment session
    throw new Error("[handleFailedPayment] No payment session amount");
  }

  if (!paymentSession.currency) {
    throw new Error("[handleFailedPayment] No payment session currency");
  }

  const { data, error } = await client
    .from("payments")
    .upsert({
      work_id: workId,
      user_id: userId,
      payment_tier: paymentTier,
      payment_intent_id: paymentSession.id,
      amount: paymentSession.amount_total / 100,
      currency: paymentSession.currency,
      status: PaymentRecordStatus.Failed,
      payload: paymentSession,
      created_at: new Date().toISOString(),
    });

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Persist a raw Stripe webhook event for auditing.
 *
 * @param client - Supabase client
 * @param type - Event type
 * @param createdAt - Timestamp from Stripe
 * @param object - Object type
 * @param apiVersion - Stripe API version
 * @param livemode - Whether this event occurred in live mode
 * @param pendingWebhooks - Pending webhook count
 * @param request - Request info from Stripe
 * @param data - Full event payload
 */
export async function handlePaymentEventReceived(
  client: SupabaseClient,
  type: string,
  createdAt: Date,
  object: string,
  apiVersion: string,
  livemode: boolean,
  pendingWebhooks: number,
  request: any,
  data: any
): Promise<any> {
  const dataToInsert = {
    type,
    created_at: createdAt,
    object,
    api_version: apiVersion,
    livemode,
    pending_webhooks: pendingWebhooks,
    request,
    data,
  };

  const { data: insertedData, error } = await client
    .from("payment_logs")
    .insert(dataToInsert)
    .select()
    .single();

  if (error) {
    throw new Error(`Error inserting payment log: ${error.message}`);
  }

  return insertedData;
}

/**
 * Check whether the user has already completed a payment for a given tier and optional work.
 *
 * @param client - Supabase client
 * @param userId - ID of the user to check
 * @param paymentTier - Payment tier being queried
 * @param getProductTier - Helper to retrieve the tier's primary product
 * @param getWorkByFriendlyId - Work lookup helper
 * @param workId - Optional friendly ID of the work
 * @returns Existing order data or pending status
 */
export async function verifyPayment(
  client: SupabaseClient,
  userId: string,
  paymentTier: PaymentTier,
  getProductTier: (tier: PaymentTier) => Promise<Product | null>,
  getWorkByFriendlyId: (friendlyId: string, userId: string) => Promise<Work | null>,
  workId?: string
): Promise<any> {
  const productTier = await getProductTier(paymentTier);
  if (!productTier) {
    throw new Error("Product tier not found");
  }
  let query = client
    .from("orders")
    .select("*")
    .eq("user_id", userId)
    .eq("product_id", productTier.id);

  if (workId) {
    const work = await getWorkByFriendlyId(workId, userId);

    if (!work) {
      throw new Error("Work not found");
    }

    query = query.eq("work_id", work.id);
  } else {
    query = query.is("work_id", null);
  }

  const { data, error } = await query
    .order("created_at", { ascending: false })
    .limit(1);

  if (error) {
    throw new Error(`Error verifying payment: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return { status: PaymentRecordStatus.Pending };
  }

  return data[0];
}
